import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Link, type RouteObject } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { ApiError } from './api/client';
import { RequireAuth, useMe } from './auth';
import { FeedbackProvider } from './components/ui';
import { initLang, useT } from './i18n';
import { Shell } from './layout/Shell';
import { Account } from './pages/Account';
import { Dealers } from './pages/Dealers';
import { Home } from './pages/Home';
import { Leads } from './pages/Leads';
import { Login } from './pages/Login';
import { ModelEditor } from './pages/model/ModelEditor';
import { Models } from './pages/Models';
import { PmHistory } from './pages/pm/History';
import { HistoryEditor } from './pages/pm/HistoryEditor';
import { PmHome } from './pages/pm/Home';
import { PmLineup } from './pages/pm/Lineup';
import { LineupEditor } from './pages/pm/LineupEditor';
import { PmNews } from './pages/pm/News';
import { PmSectionsPage } from './pages/pm/Sections';
import { NewsEditor } from './pages/pm/NewsEditor';
import { Publish } from './pages/Publish';
import { Texts } from './pages/Texts';
import { Users } from './pages/Users';
import { SITE } from './site';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Sin reintentos para errores de la API (4xx): solo para fallas de red.
      retry: (count, error) => !(error instanceof ApiError) && count < 2,
    },
  },
});

function NotFound() {
  const { t } = useT();
  return (
    <div className="panel empty-state">
      <strong>{t('Esta página no existe.')}</strong>
      <Link to="/" className="btn">
        {t('Ir al inicio')}
      </Link>
    </div>
  );
}

/** Las noticias de Phelon & Moore estuvieron unos días dentro de este dashboard: ahora tienen el suyo. */
function MovedToPm() {
  const { t } = useT();
  const { data: me } = useMe();
  const url = me?.dashboards?.['phelon-moore'];
  return (
    <div className="panel empty-state">
      <strong>{t('Phelon & Moore tiene ahora su propio dashboard.')}</strong>
      {url ? (
        <a href={url} className="btn btn-primary">
          {t('Ir al dashboard de Phelon & Moore')}
        </a>
      ) : (
        <span className="muted">{t('Pídele la dirección a un administrador.')}</span>
      )}
    </div>
  );
}

/** Las secciones de cada marca: MTX administra su sitio de motos; Phelon & Moore, su landing. */
const brandRoutes: RouteObject[] =
  SITE === 'phelon-moore'
    ? [
        { index: true, element: <PmHome /> },
        { path: 'noticias', element: <PmNews /> },
        { path: 'noticias/:id', element: <NewsEditor /> },
        { path: 'historia', element: <PmHistory /> },
        { path: 'historia/:id', element: <HistoryEditor /> },
        { path: 'motos', element: <PmLineup /> },
        { path: 'motos/:id', element: <LineupEditor /> },
        { path: 'secciones', element: <PmSectionsPage /> },
        { path: 'publicar', element: <Publish site="phelon-moore" /> },
      ]
    : [
        { index: true, element: <Home /> },
        { path: 'motos', element: <Models /> },
        { path: 'motos/:id', element: <ModelEditor /> },
        { path: 'puntos-de-venta', element: <Dealers /> },
        { path: 'textos', element: <Texts /> },
        { path: 'publicar', element: <Publish site="mtx" /> },
        { path: 'phelon-moore/*', element: <MovedToPm /> },
      ];

const router = createBrowserRouter([
  { path: '/entrar', element: <Login /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    children: [
      ...brandRoutes,
      { path: 'mensajes', element: <Leads /> },
      { path: 'mensajes/:id', element: <Leads /> },
      { path: 'usuarios', element: <Users /> },
      { path: 'cuenta', element: <Account /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

// Se pinta con el idioma ya decidido (y su diccionario bajado): así no se ve un parpadeo de otro idioma.
void initLang().finally(() =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <FeedbackProvider>
          <RouterProvider router={router} />
        </FeedbackProvider>
      </QueryClientProvider>
    </StrictMode>,
  ),
);
